import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  TextField,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  IconButton,
  Alert,
  Chip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Tabs,
  Tab,
  Card,
  CardContent,
} from '@mui/material';
import { Add as AddIcon, Edit as EditIcon, Delete as DeleteIcon, Link as LinkIcon } from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import { useTenant } from '../contexts/TenantContext';

// Mock data for facilities
const mockClientFacilities = [
  { id: 1, name: 'Main Campus', subdomain: 'main.acme', adminEmail: 'admin@main.acme.com', status: 'Active', users: 15, residents: 42 },
  { id: 2, name: 'West Branch', subdomain: 'west.acme', adminEmail: 'admin@west.acme.com', status: 'Active', users: 12, residents: 38 },
  { id: 3, name: 'East Branch', subdomain: 'east.acme', adminEmail: 'admin@east.acme.com', status: 'Pending', users: 8, residents: 25 },
];

const TenantManagementPage: React.FC = () => {
  const { user } = useAuth();
  const { tenant, setTenant, setCurrentTenantId } = useTenant();
  const [facilities, setFacilities] = useState<any[]>([]);
  const [activeTab, setActiveTab] = useState(0);
  const [openDialog, setOpenDialog] = useState(false);
  const [dialogMode, setDialogMode] = useState<'add' | 'edit'>('add');
  const [currentFacility, setCurrentFacility] = useState<any>(null);
  const [formData, setFormData] = useState({
    name: '',
    subdomain: '',
    adminEmail: '',
    status: 'Active',
  });
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [successMessage, setSuccessMessage] = useState('');

  useEffect(() => {
    // In a real app, this would fetch facilities based on the client
    if (user?.role === 'CLIENT_ADMIN') {
      // Filter facilities for the specific client
      setFacilities(mockClientFacilities);
    } else if (user?.role === 'SUPER_ADMIN') {
      // For super admin, we might show all facilities or allow filtering
      setFacilities(mockClientFacilities); // Using mock data for demo
    } else {
      // For facility admin, just show the current facility
      if (tenant) {
        setFacilities([{
          id: 1,
          name: tenant.name,
          subdomain: tenant.id,
          adminEmail: 'admin@facility.com',
          status: 'Active',
          users: 0,
          residents: 0,
        }]);
      }
    }
  }, [user, tenant]);

  const validateForm = () => {
    const newErrors: Record<string, string> = {};
    
    if (!formData.name.trim()) {
      newErrors.name = 'Facility name is required';
    }
    
    if (!formData.subdomain.trim()) {
      newErrors.subdomain = 'Subdomain is required';
    } else if (!/^[a-z0-9-]+$/.test(formData.subdomain)) {
      newErrors.subdomain = 'Subdomain can only contain lowercase letters, numbers, and hyphens';
    }
    
    if (!formData.adminEmail.trim()) {
      newErrors.adminEmail = 'Admin email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.adminEmail)) {
      newErrors.adminEmail = 'Please enter a valid email address';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleAddFacility = () => {
    setDialogMode('add');
    setFormData({
      name: '',
      subdomain: '',
      adminEmail: '',
      status: 'Active',
    });
    setCurrentFacility(null);
    setErrors({});
    setSuccessMessage('');
    setOpenDialog(true);
  };

  const handleEditFacility = (facility: any) => {
    setDialogMode('edit');
    setFormData({
      name: facility.name,
      subdomain: facility.subdomain,
      adminEmail: facility.adminEmail,
      status: facility.status,
    });
    setCurrentFacility(facility);
    setErrors({});
    setSuccessMessage('');
    setOpenDialog(true);
  };

  const handleDeleteFacility = (id: number) => {
    if (window.confirm(`Are you sure you want to delete facility ${id}? This action cannot be undone.`)) {
      setFacilities(facilities.filter(f => f.id !== id));
      setSuccessMessage('Facility deleted successfully');
      setTimeout(() => setSuccessMessage(''), 3000);
    }
  };

  const handleSwitchFacility = (facility: any) => {
    // In a real app, this would switch to the selected facility
    const tenantData = {
      id: facility.subdomain,
      name: facility.name,
      primaryColor: tenant?.primaryColor || '#1976d2',
    };
    
    setTenant(tenantData);
    setCurrentTenantId(facility.subdomain);
    localStorage.setItem('currentTenantId', facility.subdomain);
    
    setSuccessMessage(`Switched to ${facility.name}`);
    setTimeout(() => setSuccessMessage(''), 3000);
  };

  const handleSubmit = () => {
    if (!validateForm()) {
      return;
    }

    if (dialogMode === 'add') {
      const newFacility = {
        id: facilities.length + 1,
        name: formData.name,
        subdomain: formData.subdomain,
        adminEmail: formData.adminEmail,
        status: formData.status,
        users: 0,
        residents: 0,
      };
      
      setFacilities([...facilities, newFacility]);
      setSuccessMessage('Facility added successfully');
    } else if (currentFacility) {
      const updatedFacilities = facilities.map(f => 
        f.id === currentFacility.id 
          ? { 
              ...f, 
              name: formData.name,
              subdomain: formData.subdomain,
              adminEmail: formData.adminEmail,
              status: formData.status,
            } 
          : f
      );
      
      setFacilities(updatedFacilities);
      setSuccessMessage('Facility updated successfully');
    }
    
    setOpenDialog(false);
    setTimeout(() => setSuccessMessage(''), 3000);
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value,
    });
    
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors({
        ...errors,
        [name]: '',
      });
    }
  };

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setActiveTab(newValue);
  };

  return (
    <Box>
      <Typography variant="h4" component="h1" gutterBottom>
        {user?.role === 'CLIENT_ADMIN' ? 'My Facilities' : 
         user?.role === 'SUPER_ADMIN' ? 'Facility Management' : 
         'My Facility'}
      </Typography>
      
      {successMessage && (
        <Alert severity="success" sx={{ mb: 2 }}>
          {successMessage}
        </Alert>
      )}
      
      <Paper sx={{ p: 2, mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography variant="h6">
          {user?.role === 'CLIENT_ADMIN' ? 'Manage Your Facilities' : 
           user?.role === 'SUPER_ADMIN' ? 'Manage All Facilities' : 
           'Manage This Facility'}
        </Typography>
        {user?.role !== 'ADMIN' && user?.role !== 'NURSE' && (
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={handleAddFacility}
          >
            Add New Facility
          </Button>
        )}
      </Paper>

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Name</TableCell>
              <TableCell>Subdomain</TableCell>
              <TableCell>Admin Email</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Users</TableCell>
              <TableCell>Residents</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {facilities.map((facility) => (
              <TableRow key={facility.id}>
                <TableCell>{facility.name}</TableCell>
                <TableCell>{facility.subdomain}</TableCell>
                <TableCell>{facility.adminEmail}</TableCell>
                <TableCell>
                  <Chip 
                    label={facility.status} 
                    size="small" 
                    color={facility.status === 'Active' ? 'success' : 'warning'} 
                  />
                </TableCell>
                <TableCell>{facility.users}</TableCell>
                <TableCell>{facility.residents}</TableCell>
                <TableCell align="right">
                  {user?.role === 'CLIENT_ADMIN' && (
                    <IconButton 
                      color="primary" 
                      onClick={() => handleSwitchFacility(facility)}
                      title="Switch to this facility"
                      sx={{ mr: 1 }}
                    >
                      <LinkIcon />
                    </IconButton>
                  )}
                  {(user?.role === 'CLIENT_ADMIN' || user?.role === 'SUPER_ADMIN') && (
                    <>
                      <IconButton 
                        color="primary" 
                        onClick={() => handleEditFacility(facility)}
                        sx={{ mr: 1 }}
                      >
                        <EditIcon />
                      </IconButton>
                      <IconButton 
                        color="error" 
                        onClick={() => handleDeleteFacility(facility.id)}
                      >
                        <DeleteIcon />
                      </IconButton>
                    </>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Add/Edit Facility Dialog */}
      <Dialog open={openDialog} onClose={() => setOpenDialog(false)}>
        <DialogTitle>
          {dialogMode === 'add' ? 'Add New Facility' : 'Edit Facility'}
        </DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>
            {dialogMode === 'add' 
              ? 'Enter the details for the new healthcare facility.' 
              : 'Update the details for this healthcare facility.'}
          </DialogContentText>
          
          <TextField
            autoFocus
            margin="dense"
            name="name"
            label="Facility Name"
            type="text"
            fullWidth
            variant="outlined"
            value={formData.name}
            onChange={handleChange}
            error={!!errors.name}
            helperText={errors.name}
            sx={{ mb: 2 }}
          />
          
          <TextField
            margin="dense"
            name="subdomain"
            label="Subdomain"
            type="text"
            fullWidth
            variant="outlined"
            value={formData.subdomain}
            onChange={handleChange}
            error={!!errors.subdomain}
            helperText={errors.subdomain || "This will be used as the subdomain (e.g., 'facility.clientname.com')"}
            disabled={dialogMode === 'edit'} // Prevent changing subdomain after creation
            sx={{ mb: 2 }}
          />
          
          <TextField
            margin="dense"
            name="adminEmail"
            label="Admin Contact Email"
            type="email"
            fullWidth
            variant="outlined"
            value={formData.adminEmail}
            onChange={handleChange}
            error={!!errors.adminEmail}
            helperText={errors.adminEmail}
            sx={{ mb: 2 }}
          />
          
          <FormControl fullWidth margin="dense">
            <InputLabel id="status-select-label">Status</InputLabel>
            <Select
              labelId="status-select-label"
              id="status-select"
              name="status"
              value={formData.status}
              label="Status"
              onChange={(e) => setFormData({...formData, status: e.target.value as string})}
            >
              <MenuItem value="Active">Active</MenuItem>
              <MenuItem value="Pending">Pending</MenuItem>
              <MenuItem value="Inactive">Inactive</MenuItem>
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDialog(false)}>Cancel</Button>
          <Button onClick={handleSubmit} variant="contained">
            {dialogMode === 'add' ? 'Add Facility' : 'Update Facility'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default TenantManagementPage;